/**
 * ブラウザ内蔵 LLM（Chrome/Edge の Prompt API）を薄くラップする。
 *
 * <p>端末内で推論し、給与データをネットワークへ送信しない（憲章 IV / FR-003）。
 * Web Worker では Prompt API が使えないためメインスレッドで実行する。</p>
 */

/** 端末内 LLM が抽出する給与項目。 */
export interface ParsedSlip {
  salaryDate: string | null;
  isBonus?: boolean;
  grossSalary?: number;
  healthInsurance?: number;
  employmentInsurance?: number;
  nationalPension?: number;
  incomeTax?: number;
  localTax?: number;
  definedContribution?: number;
  netSalary?: number;
}

/** 内蔵 LLM の利用可否。`available` のときのみ端末内解析に用いる（MI-6）。 */
export type BrowserLlmAvailability =
  | 'available'
  | 'downloadable'
  | 'downloading'
  | 'unavailable'
  | 'unknown';

interface LanguageModelInput {
  type: 'text';
  languages?: string[];
}

interface LanguageModelAvailabilityOptions {
  expectedInputs?: LanguageModelInput[];
  expectedOutputs?: LanguageModelInput[];
}

interface LanguageModelCreateOptions extends LanguageModelAvailabilityOptions {
  initialPrompts?: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>;
}

interface LanguageModelPromptOptions {
  responseConstraint?: object;
  signal?: AbortSignal;
}

interface LanguageModelSession {
  prompt(input: string, options?: LanguageModelPromptOptions): Promise<string>;
  destroy(): void;
}

interface LanguageModelStatic {
  availability(options?: LanguageModelAvailabilityOptions): Promise<string>;
  create(options?: LanguageModelCreateOptions): Promise<LanguageModelSession>;
}

declare global {
  interface Window {
    LanguageModel?: LanguageModelStatic;
  }
}

/** `availability` と `create` で同一の構成を渡す（MI-1）。 */
const EXPECTED_INPUTS: LanguageModelInput[] = [{ type: 'text', languages: ['ja', 'en'] }];
const EXPECTED_OUTPUTS: LanguageModelInput[] = [{ type: 'text', languages: ['ja'] }];

/** 追跡する主要項目（信頼度の算出に用いる）。 */
const TRACKED_KEYS: Array<keyof ParsedSlip> = [
  'salaryDate',
  'grossSalary',
  'netSalary',
  'healthInsurance',
  'employmentInsurance',
  'nationalPension',
  'incomeTax',
  'localTax',
  'definedContribution',
];

/** `responseConstraint` に渡す JSON Schema。 */
const RESPONSE_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['salaryDate', 'grossSalary', 'netSalary'],
  properties: {
    salaryDate: { type: 'string' },
    grossSalary: { type: 'number' },
    healthInsurance: { type: 'number' },
    employmentInsurance: { type: 'number' },
    nationalPension: { type: 'number' },
    incomeTax: { type: 'number' },
    localTax: { type: 'number' },
    definedContribution: { type: 'number' },
    netSalary: { type: 'number' },
    isBonus: { type: 'boolean' },
  },
};

const SYSTEM_PROMPT =
  'あなたは日本の給与明細・賞与明細を解析する専門家です。与えられたテキストから給与項目を抽出し、指定されたJSONのみで回答してください。';

const CREATE_OPTIONS: LanguageModelCreateOptions = {
  expectedInputs: EXPECTED_INPUTS,
  expectedOutputs: EXPECTED_OUTPUTS,
  initialPrompts: [{ role: 'system', content: SYSTEM_PROMPT }],
};

/** 事前生成したセッション（transient activation 対策, MI-2）。 */
let prewarmedSession: LanguageModelSession | null = null;
let prewarmPromise: Promise<void> | null = null;

function getLanguageModel(): LanguageModelStatic | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return window.LanguageModel;
}

/** 内蔵 LLM の利用可否を詳細に返す。 */
export async function getBrowserLlmAvailability(): Promise<BrowserLlmAvailability> {
  const languageModel = getLanguageModel();
  if (!languageModel) {
    return 'unavailable';
  }
  try {
    const availability = await languageModel.availability({
      expectedInputs: EXPECTED_INPUTS,
      expectedOutputs: EXPECTED_OUTPUTS,
    });
    switch (availability) {
      case 'available':
      case 'downloadable':
      case 'downloading':
      case 'unavailable':
        return availability;
      default:
        return 'unknown';
    }
  } catch {
    return 'unknown';
  }
}

/** 内蔵 LLM が利用可能か（`'available'` のときのみ true, MI-6）。 */
export async function isBrowserLlmAvailable(): Promise<boolean> {
  return (await getBrowserLlmAvailability()) === 'available';
}

/**
 * ファイル選択直後（transient activation が有効な間）にセッション生成を先行させる（MI-2）。
 * 生成に失敗しても例外は投げず、抽出時に再生成する。
 */
export function prewarmBrowserLlm(): void {
  const languageModel = getLanguageModel();
  if (!languageModel || prewarmedSession || prewarmPromise) {
    return;
  }
  prewarmPromise = languageModel
    .create(CREATE_OPTIONS)
    .then((session) => {
      prewarmedSession = session;
    })
    .catch(() => {
      // 事前生成の失敗は無視し、抽出時にサーバフォールバックへ切り替える
    })
    .then(() => {
      prewarmPromise = null;
    });
}

/** 事前生成セッションを破棄する（未使用時の解放・テスト用）。 */
export function destroyBrowserLlmSession(): void {
  if (prewarmedSession) {
    try {
      prewarmedSession.destroy();
    } catch {
      // destroy 失敗は無視
    }
    prewarmedSession = null;
  }
  prewarmPromise = null;
}

/**
 * 端末内 LLM でテキストから給与項目を抽出する。
 *
 * <p>事前生成セッションがあれば再利用し、無ければ生成する。</p>
 *
 * @throws 内蔵 LLM が無い・応答が不正・必須項目欠落の場合（呼び出し側でサーバへ切替）
 */
export async function extractWithBrowserLlm(text: string): Promise<ParsedSlip> {
  const session = await acquireSession();
  try {
    const raw = await session.prompt(buildPrompt(text), { responseConstraint: RESPONSE_SCHEMA });
    return parseSlip(raw);
  } finally {
    session.destroy();
  }
}

async function acquireSession(): Promise<LanguageModelSession> {
  if (prewarmPromise) {
    await prewarmPromise;
  }
  if (prewarmedSession) {
    const session = prewarmedSession;
    prewarmedSession = null;
    return session;
  }
  const languageModel = getLanguageModel();
  if (!languageModel) {
    throw new Error('ブラウザ内蔵LLMが利用できません');
  }
  return languageModel.create(CREATE_OPTIONS);
}

/** 端末内 LLM の応答を `ParsedSlip` へ正規化する（スキーマ違反は例外）。 */
export function parseSlip(raw: string): ParsedSlip {
  if (!raw) {
    throw new Error('端末内LLMの応答が空です');
  }
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  const jsonText = start >= 0 && end > start ? raw.substring(start, end + 1) : raw;

  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonText);
  } catch {
    throw new Error('端末内LLMの応答をJSONとして解析できません');
  }
  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('端末内LLMの応答が不正です');
  }

  const object = parsed as Record<string, unknown>;
  const grossSalary = toNumber(object.grossSalary);
  const netSalary = toNumber(object.netSalary);
  if (grossSalary === undefined || netSalary === undefined) {
    throw new Error('端末内LLMの応答に必須項目（総支給額・差引支給額）がありません');
  }

  return {
    salaryDate: toDateString(object.salaryDate),
    isBonus: typeof object.isBonus === 'boolean' ? object.isBonus : undefined,
    grossSalary,
    healthInsurance: toNumber(object.healthInsurance),
    employmentInsurance: toNumber(object.employmentInsurance),
    nationalPension: toNumber(object.nationalPension),
    incomeTax: toNumber(object.incomeTax),
    localTax: toNumber(object.localTax),
    definedContribution: toNumber(object.definedContribution),
    netSalary,
  };
}

/** 追跡項目の充足率を信頼度として返す（FR-006、検証前の初期値）。 */
export function computeBrowserConfidence(parsed: ParsedSlip): number {
  const present = TRACKED_KEYS.filter(
    (key) => parsed[key] !== undefined && parsed[key] !== null,
  ).length;
  return present / TRACKED_KEYS.length;
}

/** LLM 応答の日付表記を `YYYY-MM-DD` に正規化する。不正なら null。 */
export function toDateString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const match = value.match(/(\d{4})[-/年](\d{1,2})[-/月](\d{1,2})/);
  if (!match) {
    return null;
  }
  return `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')}`;
}

function buildPrompt(text: string): string {
  return `以下の給与明細または賞与明細のテキストから情報を抽出し、JSONのみで回答してください。
- 金額はカンマを除いた数値
- salaryDate は YYYY-MM-DD
- 賞与の場合は isBonus を true
- 値が無い項目は 0（または null）

テキスト内容:
${text}`;
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.replace(/[^0-9.-]/g, '');
    if (normalized === '') {
      return undefined;
    }
    const number = Number(normalized);
    return Number.isFinite(number) ? number : undefined;
  }
  return undefined;
}
